import {FormArray, FormControl, FormGroup, Validators} from '@angular/forms';

import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    IndividualExtendedModel,
    IndividualIdentificationNumberInputModel,
    IndividualsService,
} from '@spa/api';

import {BaseControl, Helper} from '../../../../shared/classes';

import {
    IdentificationNumberControls,
    IdentificationNumberControlsInterface,
    IdentificationNumberMaxLength,
    IdentificationNumberSelector,
    IdentificationNumberUseTaxNumberSelector,
    TaxDetailCountryIdSelector,
    TaxDetailTaxNumberSelector,
} from 'appAssets/descriptors';

import {TaxDetailsForm} from './tax-details.form';

/**
 * CRM-5286 / CRM-5232: the Identification Number capture block on the tax step.
 *
 * The client must do one of two things before the step can be completed: tick "ID number is the
 * same as tax number", or enter a country specific identification number. Which of the two is
 * enforced by moving `required` on and off the text control, because this codebase has no way to
 * render a FormGroup-level validator's message.
 *
 * The checkbox itself only sends a flag. The copy of the tax number is made by the backend, from
 * the tax record whose country matches one of the client's nationalities - so this form does not
 * duplicate that rule, it only decides whether the checkbox can be offered at all.
 */
export class IdentificationNumberForm {
    private readonly individual: IndividualExtendedModel;
    private readonly individualsService: IndividualsService;

    /**
     * CRM-5328: the tax step's own form, so the checkbox can be offered on what has been typed rather
     * than on what has been saved. They are siblings on the same step, created together by
     * IndividualRegistrationForm; optional so the block still behaves without one.
     */
    private readonly taxDetailsForm: TaxDetailsForm;

    public form: FormGroup;
    public controls: IdentificationNumberControlsInterface;

    constructor(
        individual: IndividualExtendedModel,
        individualsService: IndividualsService,
        taxDetailsForm: TaxDetailsForm = null
    ) {
        this.individual = individual;
        this.individualsService = individualsService;
        this.taxDetailsForm = taxDetailsForm;
    }

    /**
     * CRM-5328: now that the offer follows what is typed, it can also be taken away - editing the tax
     * country to one that is not a nationality, or clearing the tax number, hides a checkbox that may
     * already be ticked. Left ticked, it answers the question with a flag the backend would refuse,
     * while the text field stays hidden and not required, so the step would save with no usable
     * answer. Clearing the control runs the binding above, which puts `required` back.
     */
    private watchTaxNumberAvailability(): void {
        const taxDetailsFormArray = this.taxDetailsFormArray();

        if (!taxDetailsFormArray) {
            return;
        }

        taxDetailsFormArray.valueChanges.subscribe(() => {
            const useTaxNumberControl = this.form.get(IdentificationNumberUseTaxNumberSelector);

            if (useTaxNumberControl && useTaxNumberControl.value && !this.canUseTaxNumber()) {
                useTaxNumberControl.setValue(false);
            }
        });
    }

    /** The tax step's rows, when this form was given the tax form beside it. */
    private taxDetailsFormArray(): FormArray {
        return this.taxDetailsForm
            && this.taxDetailsForm.form
            && this.taxDetailsForm.form.get(this.taxDetailsForm.taxDetailsFormArraySelector) as FormArray;
    }

    /**
     * The tax rows as they stand on screen, falling back to the persisted records when this form was
     * built without the tax form beside it.
     */
    private enteredTaxDetails(): { countryId: string, taxNumber: string }[] {
        const taxDetailsFormArray = this.taxDetailsFormArray();

        if (!taxDetailsFormArray) {
            return ((this.individual && this.individual.taxDetails) || [])
                .map((taxDetail) => ({countryId: taxDetail.countryId, taxNumber: taxDetail.taxNumber}));
        }

        return taxDetailsFormArray.controls.map((formGroup: FormGroup) => {
            const countryControl = formGroup.get(TaxDetailCountryIdSelector);
            const taxNumberControl = formGroup.get(TaxDetailTaxNumberSelector);

            return {
                countryId: countryControl ? String(countryControl.value || '') : '',
                taxNumber: taxNumberControl ? String(taxNumberControl.value || '') : ''
            };
        });
    }

    initForm(): void {
        this.form = new FormGroup({});
        this.controls = IdentificationNumberControls();

        if (this.individual && this.individual.identificationNumber) {
            this.controls.identificationNumber.value = this.individual.identificationNumber;
        }

        const useTaxNumberControl = new FormControl(
            this.controls.useTaxNumber.value,
            this.controls.useTaxNumber.validators
        );

        useTaxNumberControl.valueChanges.subscribe((value: boolean) => {
            this.bindIdentificationNumberValidators(value);
        });

        this.form.addControl(this.controls.useTaxNumber.name, useTaxNumberControl);

        this.form.addControl(this.controls.identificationNumber.name, new FormControl(
            this.controls.identificationNumber.value,
            this.controls.identificationNumber.validators
        ));

        this.bindIdentificationNumberValidators(this.controls.useTaxNumber.value as boolean);
        this.watchTaxNumberAvailability();
    }

    /**
     * AC-F1: one of the two answers is mandatory. Ticking the checkbox is an answer, so the text
     * field stops being required and is cleared - leaving a stale typed value behind would be
     * misleading, since the backend ignores it when the flag is set.
     */
    bindIdentificationNumberValidators(useTaxNumber: boolean): void {
        const control = this.form.get(IdentificationNumberSelector);

        if (!control) {
            return;
        }

        control.clearValidators();

        if (useTaxNumber) {
            control.setValidators([Validators.maxLength(IdentificationNumberMaxLength)]);
            control.setValue('', {emitEvent: false});
        } else {
            control.setValidators([Validators.required, Validators.maxLength(IdentificationNumberMaxLength)]);
        }

        control.updateValueAndValidity();
    }

    /**
     * AC-F3: the checkbox is only offered when one of the client's tax records is for a country of
     * their nationality, because that is the record the backend would copy from. The backend
     * refuses the flag in the same situation, so this is a courtesy rather than the guard.
     *
     * CRM-5328: answered from the tax form rather than from individual.taxDetails. The persisted
     * records are empty for the whole of this step - it is shown precisely because there are none
     * yet - so the checkbox could never appear while the client was being enrolled. Reading the form
     * offers it as soon as the tax country and number are entered, and still answers from the
     * persisted records afterwards, because the form is initialised from them.
     */
    canUseTaxNumber(): boolean {
        const nationalities = (this.individual && this.individual.nationalities) || [];

        if (nationalities.length === 0) {
            return false;
        }

        return this.enteredTaxDetails().some((taxDetail: { countryId: string, taxNumber: string }) =>
            !!taxDetail.taxNumber
            && !!taxDetail.countryId
            && nationalities.some(nationality =>
                !!nationality
                && (nationality.toString().toUpperCase() === taxDetail.countryId.toUpperCase())));
    }

    showErrors(
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; },
        control: BaseControl
    ): void {
        const formControl = this.form.get(control.name);

        if (formControl && formControl.invalid) {
            const errorMessages = Object.keys(formControl.errors).map((errorKey: string) => {
                return messageFunction(control.placeholder, IdentificationNumberMaxLength)[errorKey];
            });

            control.setErrors(errorMessages);
        }
    }

    validateForm(
        messageFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string; }
    ): boolean {
        if (this.form.invalid) {
            Object.keys(this.controls).forEach((controlKey: string) => {
                this.showErrors(messageFunction, this.controls[controlKey]);
            });

            return false;
        }

        return true;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        if (!this.form.dirty) {
            return null;
        }

        const useTaxNumber = !!this.form.get(IdentificationNumberUseTaxNumberSelector).value;
        const identificationNumber = this.form.get(IdentificationNumberSelector).value;

        const model = new IndividualIdentificationNumberInputModel(
            this.individual.id,
            useTaxNumber,
            useTaxNumber ? null : identificationNumber
        );

        return this.individualsService.updateIdentificationNumber(model)
            .pipe(
                map((response: ApiResponse<null>) => {
                    // BaseApiService.put() maps every 2xx to ApiResponse(200), so a 204 never reaches
                    // here - checking for NoContent would fail every successful save.
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse<boolean>(response);
                    }

                    return Helper.createSuccessResponse<boolean>(true);
                })
            );
    }
}
